<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '600px'"
		@click:outside="closeDialog"
		@keydown.esc="closeDialog"
	>
		<!-- @keydown.enter="setValue" -->
		<v-card class="sgnl-pb-4">
			<v-card-title class="dialog-header sgnl-subtitle-medium">
				{{ applicationInfo.titleText }}
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="closeDialog"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<LoadingCard
				v-if="repoAccessState.loading"
				loadingText="Loading Application Details"
			/>

			<v-card-text
				v-else
				class="dialog-body"
			>
				<VideoPlayerComponent
					v-if="demoVideoURL"
					:videoURL="demoVideoURL"
					class="video"
				/>

				<div v-if="repoData.description">
					{{ repoData.description }}
				</div>
				<AlertMessage
					v-else-if="repoAccessState.error"
					:show-alert="true"
					type="warning"
					:outlined="true"
				>
					An Error occurred while fetching the application description.
					{{ repoAccessState.message }}
				</AlertMessage>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					:disabled="!applicationInfo.applicationURL"
					target="_blank"
					:href="applicationInfo.applicationURL"
				>
					<LogoGithub16 />
					Open on GitHub
					<Launch16 />
				</v-btn>
				<v-btn
					variant="flat"
					color="primary"
					:disabled="!applicationInfo.applicationURL || isConnected"
					@click="connectRepo"
				>
					<template v-if="isConnected">
						<Checkmark16 />
						Connected
					</template>
					<template v-else>
						<BookmarkAdd16 />
						Connect Repository
					</template>
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

// Libraries
import axios from "axios";

// Components
import VideoPlayerComponent from "@/components/Common/VideoPlayerComponent.vue";
import LoadingCard from "@/components/LoadingCard.vue";
import AlertMessage from "@/components/Common/AlertMessage.vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import LogoGithub16 from "@carbon/icons-vue/es/logo--github/16";
import Launch16 from "@carbon/icons-vue/es/launch/16";
import BookmarkAdd16 from "@carbon/icons-vue/es/bookmark--add/16";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";

// Stores
import { useRepositoriesStore } from "@/stores/repositories";

// Utilities
import { getRepoData } from "@/js/githubClient";
import { generateGithubResponseErrorMessage } from "@/js/githubUtilities";
import { navigateToConnectRepo } from "@/js/repositoryUtilities";

// Types
import { ApplicationSlideInfo } from "@/types/home";
import type { Endpoints } from "@octokit/types";
import { RemoteAccessState } from "@/types/general";

type ComponentData = {
	repoData: any;
	loading: boolean;
	repoAccessState: RemoteAccessState;
};

export default defineComponent({
	name: "ApplicationDetailDialog",
	components: {
		VideoPlayerComponent,
		LoadingCard,
		AlertMessage,
		Close16,
		LogoGithub16,
		Launch16,
		BookmarkAdd16,
		Checkmark16,
	},
	emits: ["update:showDialog"],
	props: {
		applicationInfo: { type: Object as PropType<ApplicationSlideInfo>, required: true },
		showDialog: { type: Boolean, default: false },
		initialRepoData: {
			type: Object as PropType<Endpoints["GET /repos/{owner}/{repo}"]["response"]["data"]>,
			required: false,
		},
	},
	setup() {
		const repositoriesStore = useRepositoriesStore();
		return { repositoriesStore };
	},
	data: (): ComponentData => ({
		repoData: {},
		loading: false,
		repoAccessState: { loading: false, error: false, message: "" },
	}),
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		demoVideoURL(): string | undefined {
			return this.applicationInfo.demoVideoURL
				? new URL(this.applicationInfo.demoVideoURL, import.meta.env.VITE_ASSET_SERVER_URL).toString()
				: undefined;
		},
		isConnected(): boolean {
			if (!this.applicationInfo.applicationURL) {
				return false;
			}
			return this.repositoriesStore.isURLConnected(this.applicationInfo.applicationURL);
		},
	},
	methods: {
		closeDialog() {
			this.$emit("update:showDialog", false);
		},
		connectRepo() {
			navigateToConnectRepo(this.$router, this.applicationInfo.applicationURL);
		},
		getExampleInfoFromGithub() {},
		async getRepoData() {
			this.repoAccessState.loading = true;
			try {
				const applicationURL = new URL(this.applicationInfo.applicationURL);
				const resp = await getRepoData(applicationURL.pathname.slice(1));
				this.repoData = resp.data;
			} catch (error) {
				if (axios.isAxiosError(error)) {
					this.repoAccessState.error = true;
					this.repoAccessState.message = generateGithubResponseErrorMessage(error, false);
				}
			} finally {
				this.repoAccessState.loading = false;
			}
		},
	},
	watch: {
		// Watch for changes in the entire applicationInfo object
		applicationInfo: {
			handler() {
				this.getRepoData();
			},
			deep: true,
			immediate: false,
		},
	},
	mounted() {
		if (this.initialRepoData) {
			this.repoData = this.initialRepoData;
		} else {
			this.getRepoData();
		}
	},
});
</script>

<style scoped>
.video {
	width: 100%;
	object-fit: cover;
	aspect-ratio: 16/9;
}
</style>
